import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AIResult } from './types/ai.types';

interface Props { result: AIResult; }

// Columns to hide from the table — internal use only
const HIDDEN_COLUMNS = ['ExchangeRate', 'BaseCurrency', 'ArtworkId'];

// Format column header
const formatColumnHeader = (key: string, baseCurrency: string): string => {
    if (key === 'ConvertedAmount') return `Value (${baseCurrency})`;
    if (key === 'ValuationAmount') return 'Value';
    if (key === 'InsuranceAmount') return 'Insurance Value';
    if (key === 'LowerRangeAmount') return 'Value';
    if (key === 'ArtWorkTitle') return 'Title';
    if (key === 'ArtistName') return 'Artist';
    if (key === 'ValuationDate') return 'Last Valued';
    if (key === 'StatusDate') return 'Status Date';
    if (key === 'CurrencyCode') return 'Currency';
    return key.replace(/([A-Z])/g, ' $1').trim();
};

export const ResultsRenderer: React.FC<Props> = ({ result }) => {

    // Read base currency from the first row that has it
    const baseCurrency = result.data?.find(r => r['BaseCurrency'])?.[
        'BaseCurrency'] as string ?? 'GBP';

    const currencySymbols: Record<string, string> = {
        GBP: '£', USD: '$', EUR: '€', CHF: 'Fr ',
        JPY: '¥', AUD: 'A$', CAD: 'C$',
    };

    const symbol = currencySymbols[baseCurrency] ?? `${baseCurrency} `;

    // Format cell values
    const formatCellValue = (key: string, value: unknown): string => {
        if (value === null || value === undefined || value === '') return '—';

        if (key === 'ConvertedAmount') {
            const num = Number(value);
            return isNaN(num) ? '—'
                : `${symbol}${num.toLocaleString('en-GB', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                })}`;
        }

        if (key === 'ValuationAmount' || key === 'InsuranceAmount' ||
            key === 'LowerRangeAmount') {
            const num = Number(value);
            return isNaN(num) ? '—'
                : num.toLocaleString('en-GB', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                });
        }

        if (key === 'ValuationDate' || key === 'StatusDate') {
            return value
                ? new Date(String(value)).toLocaleDateString('en-GB')
                : '—';
        }

        return String(value);
    };

    switch (result.visualizationType) {

        case 'table':
            if (!result.data?.length) return null;
            const allCols = result.columns ?? Object.keys(result.data[0]);
            const cols = allCols.filter(c => !HIDDEN_COLUMNS.includes(c));

            return (
                <Paper variant="outlined" sx={{ overflow: 'hidden', mt: 1, borderRadius: 1.5 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {cols.map(col => (
                                        <th key={col} style={{
                                            padding: '6px 10px',
                                            textAlign: col === 'GBPEquivalent' ||
                                                col.includes('Amount') ? 'right' : 'left',
                                            fontSize: 11,
                                            fontWeight: 500,
                                            backgroundColor: 'var(--color-background-secondary)',
                                            color: 'var(--color-text-secondary)',
                                            borderBottom: '0.5px solid var(--color-border-tertiary)',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {formatColumnHeader(col, baseCurrency)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {result.data.map((row, i) => (
                                    <tr key={i}>
                                        {cols.map(col => (
                                            <td key={col} style={{
                                                padding: '5px 10px',
                                                fontSize: 12,
                                                textAlign: col === 'GBPEquivalent' ||
                                                    col.includes('Amount') ? 'right' : 'left',
                                                borderBottom: i < result.data!.length - 1
                                                    ? '0.5px solid var(--color-border-tertiary)'
                                                    : 'none',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                {formatCellValue(col, row[col])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>

                    {/*Update the total footer*/}
                    {result.data.some(r => r['ConvertedAmount']) && (
                        <Box sx={{
                            px: 1.25, py: 0.875,
                            borderTop: '0.5px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: 'background.secondary',
                        }}>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                Total ({baseCurrency} equivalent)
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                {symbol}{result.data
                                    .reduce((sum, r) => sum + Number(r['ConvertedAmount'] ?? 0), 0)
                                    .toLocaleString('en-GB', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            );

        case 'chart':
            if (!result.data?.length || !result.chartConfig) return null;
            const { labelKey, valueKey, title } = result.chartConfig;
            return (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 1.5 }}>
                    {title && (
                        <Typography sx={{
                            fontSize: 11, fontWeight: 500, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1
                        }}>
                            {title}
                        </Typography>
                    )}
                    <ResponsiveContainer width="100%" height={120}>
                        <BarChart data={result.data} barCategoryGap="30%">
                            <XAxis dataKey={labelKey} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip
                                contentStyle={{ fontSize: 11, border: '0.5px solid var(--color-border-tertiary)', borderRadius: 6 }}
                                cursor={{ fill: 'var(--color-background-secondary)' }}
                            />
                            <Bar dataKey={valueKey} radius={[3, 3, 0, 0]}>
                                {result.data.map((_, index) => (
                                    <Cell key={index} fill="#EEEDFE" />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            );

        case 'metric':
            if (!result.data?.length) return null;

            // If multiple rows returned (e.g. multi-currency), 
            // sum numeric TotalWorks across all rows
            const aggregated = result.data.reduce((acc, row) => {
                Object.entries(row).forEach(([key, value]) => {
                    if (key.toLowerCase().includes('total') ||
                        key.toLowerCase().includes('count')) {
                        acc[key] = ((acc[key] as number) || 0) + Number(value);
                    } else {
                        acc[key] = value; // keep last value for non-numeric fields
                    }
                });
                return acc;
            }, {} as Record<string, unknown>);

            return (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {Object.entries(aggregated).map(([key, val]) => (
                        <Box key={key} sx={{
                            flex: '1 1 auto', minWidth: 80,
                            bgcolor: 'background.default',
                            borderRadius: 1.5,
                            p: 1,
                            border: '0.5px solid',
                            borderColor: 'divider',
                        }}>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                            </Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                                {typeof val === 'number'
                                    ? val.toLocaleString()
                                    : String(val)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );

        default:
            return null;
    }
};